// Typed wrappers for the embedded NZB downloader (`/api/v1/nzb/*`).
// Mirrors the resource layout of the backend: status, disk, queue, history,
// servers, categories, general.

import { api } from "@/lib/api";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export type QueueState =
  | "queued"
  | "downloading"
  | "paused"
  | "fetching"
  | "post_processing"
  | "unknown";

/** Per-news-server contribution to a single download. The engine
 *  tracks one row per server that supplied any articles for the job —
 *  surface it so the UI can show "Newshosting + Eweka (cascaded)"
 *  chips and the user can see at a glance which provider delivered
 *  the bytes. Important visibility for multi-server deployments where
 *  a fallback rescued a release the primary couldn't carry. */
export interface ServerStat {
  server_id: string;
  server_name: string;
  articles_downloaded: number;
  articles_failed: number;
  bytes_downloaded: number;
}

export interface QueueItem {
  id: string;
  name: string;
  category: string | null;
  size: number;
  downloaded: number;
  /** 0..1 ratio. */
  progress: number;
  state: QueueState;
  eta_secs: number | null;
  priority: number;
  /** Per-server tally; empty until the engine has pulled at least
   *  one article. Single-server deployments always have at most one
   *  entry here. */
  servers: ServerStat[];
}

export interface HistoryItem {
  id: string;
  name: string;
  status: "completed" | "failed";
  completed_at: string;
  bytes: number;
  error: string | null;
  category: string | null;
  /** Per-server tally for this completed job. */
  servers: ServerStat[];
}

export interface NzbStatus {
  speed_bps: number;
  paused: boolean;
  queue_count: number;
  history_count: number;
  max_active: number;
}

export interface NzbDisk {
  total_bytes: number;
  free_bytes: number;
  complete_dir: string;
  incomplete_dir: string;
}

export interface NewsServer {
  id: number;
  name: string;
  host: string;
  port: number;
  ssl: boolean;
  enabled: boolean;
  priority: number;
  connections: number;
  username: string | null;
}

export interface CreateNewsServer {
  name: string;
  host: string;
  port: number;
  ssl: boolean;
  enabled?: boolean;
  priority?: number;
  connections: number;
  username?: string | null;
  password?: string | null;
}

export interface UpdateNewsServer {
  name?: string;
  host?: string;
  port?: number;
  ssl?: boolean;
  enabled?: boolean;
  priority?: number;
  connections?: number;
  username?: string | null;
  /** Omit (undefined) to keep existing; null to clear. */
  password?: string | null;
}

export interface NzbCategory {
  name: string;
  output_dir: string | null;
  /** 0=None, 1=Repair, 2=Repair+Unpack, 3=Repair+Unpack+Delete. */
  post_processing: number;
}

export interface CreateNzbCategory {
  name: string;
  output_dir?: string | null;
  post_processing?: number;
}

export interface UpdateNzbCategory {
  output_dir?: string | null;
  post_processing?: number;
}

export interface NzbGeneral {
  speed_limit_bps: number;
  max_active_downloads: number;
  history_retention_count: number | null;
}

export interface NzbTestResult {
  ok: boolean;
  message: string | null;
}

export interface UploadNzbOptions {
  category?: string | null;
  priority?: number;
}

export interface AddUrlOptions {
  category?: string | null;
  priority?: number;
}

// -----------------------------------------------------------------------------
// API
// -----------------------------------------------------------------------------

export const nzbApi = {
  // ---- Status / disk -------------------------------------------------------
  status: () => api<NzbStatus>("/api/v1/nzb/status"),
  disk: () => api<NzbDisk>("/api/v1/nzb/disk"),

  // ---- Queue ---------------------------------------------------------------
  queue: {
    list: () => api<QueueItem[]>("/api/v1/nzb/queue"),
    /** Multipart NZB file upload. */
    upload: (file: File, opts: UploadNzbOptions = {}) => {
      const fd = new FormData();
      fd.append("nzb", file);
      if (opts.category) fd.append("category", opts.category);
      if (opts.priority !== undefined)
        fd.append("priority", String(opts.priority));
      return api<QueueItem>("/api/v1/nzb/queue", {
        method: "POST",
        body: fd,
      });
    },
    /** Add by URL (the backend fetches it). */
    addUrl: (url: string, opts: AddUrlOptions = {}) =>
      api<QueueItem>("/api/v1/nzb/queue/url", {
        method: "POST",
        body: JSON.stringify({
          url,
          category: opts.category ?? null,
          priority: opts.priority,
        }),
      }),
    pauseAll: () =>
      api<void>("/api/v1/nzb/queue/pause", { method: "POST" }),
    resumeAll: () =>
      api<void>("/api/v1/nzb/queue/resume", { method: "POST" }),
    pause: (id: string) =>
      api<void>(`/api/v1/nzb/queue/${encodeURIComponent(id)}/pause`, {
        method: "POST",
      }),
    resume: (id: string) =>
      api<void>(`/api/v1/nzb/queue/${encodeURIComponent(id)}/resume`, {
        method: "POST",
      }),
    setPriority: (id: string, priority: number) =>
      api<void>(`/api/v1/nzb/queue/${encodeURIComponent(id)}/priority`, {
        method: "PUT",
        body: JSON.stringify({ priority }),
      }),
    remove: (id: string) =>
      api<void>(`/api/v1/nzb/queue/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
  },

  // ---- History -------------------------------------------------------------
  history: {
    list: () => api<HistoryItem[]>("/api/v1/nzb/history"),
    remove: (id: string) =>
      api<void>(`/api/v1/nzb/history/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    clear: () => api<void>("/api/v1/nzb/history", { method: "DELETE" }),
  },

  // ---- News servers --------------------------------------------------------
  servers: {
    list: () => api<NewsServer[]>("/api/v1/nzb/servers"),
    get: (id: number) => api<NewsServer>(`/api/v1/nzb/servers/${id}`),
    create: (body: CreateNewsServer) =>
      api<NewsServer>("/api/v1/nzb/servers", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    update: (id: number, body: UpdateNewsServer) =>
      api<NewsServer>(`/api/v1/nzb/servers/${id}`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    remove: (id: number) =>
      api<void>(`/api/v1/nzb/servers/${id}`, { method: "DELETE" }),
    /** Test a saved server (by id). */
    test: (id: number) =>
      api<NzbTestResult>(`/api/v1/nzb/servers/${id}/test`, {
        method: "POST",
      }),
    /** Test an unsaved configuration without persisting. */
    testConfig: (body: CreateNewsServer) =>
      api<NzbTestResult>("/api/v1/nzb/servers/test", {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },

  // ---- Categories ----------------------------------------------------------
  categories: {
    list: () => api<NzbCategory[]>("/api/v1/nzb/categories"),
    get: (name: string) =>
      api<NzbCategory>(`/api/v1/nzb/categories/${encodeURIComponent(name)}`),
    create: (body: CreateNzbCategory) =>
      api<NzbCategory>("/api/v1/nzb/categories", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    update: (name: string, body: UpdateNzbCategory) =>
      api<NzbCategory>(`/api/v1/nzb/categories/${encodeURIComponent(name)}`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    remove: (name: string) =>
      api<void>(`/api/v1/nzb/categories/${encodeURIComponent(name)}`, {
        method: "DELETE",
      }),
  },

  // ---- General settings ----------------------------------------------------
  general: {
    get: () => api<NzbGeneral>("/api/v1/nzb/general"),
    update: (body: NzbGeneral) =>
      api<NzbGeneral>("/api/v1/nzb/general", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
  },
};

// -----------------------------------------------------------------------------
// Helpers (used by multiple pages)
// -----------------------------------------------------------------------------

/** "1.2 MB/s" / "640 KB/s" / "0 B/s" — auto-pick a unit. */
export function formatSpeed(bps: number): string {
  if (!Number.isFinite(bps) || bps <= 0) return "0 B/s";
  const units = ["B/s", "KB/s", "MB/s", "GB/s"];
  let n = bps;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n >= 100 || i === 0 ? 0 : n >= 10 ? 1 : 2)} ${units[i]}`;
}

/** "12.3 GB" / "640 MB" — for sizes / disk. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n >= 100 || i === 0 ? 0 : n >= 10 ? 1 : 2)} ${units[i]}`;
}

/** "5m 12s" / "1h 04m" / "—". */
export function formatEta(secs: number | null | undefined): string {
  if (secs == null || !Number.isFinite(secs) || secs <= 0) return "—";
  const s = Math.floor(secs);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m < 60) return `${m}m ${String(r).padStart(2, "0")}s`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return `${h}h ${String(rm).padStart(2, "0")}m`;
}

export const POST_PROCESSING_LABELS: Record<number, string> = {
  0: "None",
  1: "Repair",
  2: "Repair + Unpack",
  3: "Repair + Unpack + Delete",
};
